/**
 * Shaman kit identity (plan 29 F9, lane CA 2026-10-11).
 *
 * Authored looks for the reachable shaman abilities that resolved to a
 * school default, an unregistered name or another kit's look: Lightning
 * Touch, Stormstrike, Flame Shock, Chain Lightning and Chain Heal named defs
 * that were never registered (the map played hit_lightning / hit_fire /
 * hit_heal — Stormstrike was Lightning Touch's disc, Chain Heal the priest's
 * gold holy_heal); Lightning Bolt borrowed the mage's lightning_strike; Ghost
 * Wolf's form was an unregistered name; the totems' ticks played the school
 * splash. Each def is named by the ability's TOML (`cast_effect`,
 * `impact_effect`, `wind_up_effect`).
 *
 * Lightning Touch (the auto, DJ's reference) keeps its 'lightning' swing —
 * the bolt from the hand and the contact spark; only its confirmed impact is
 * now an authored teal crackle that matches it. Stormstrike has its own
 * 'storm' swing (styledSwing.ts) and thunderclap.
 *
 * Colours follow each TOML `aura_color`: Lightning Touch teal, Lightning
 * Bolt sky blue, Chain Lightning violet, Stormstrike storm blue, Flame Shock
 * orange, Searing Totem amber, Healing Stream sea green, Chain Heal green,
 * Ghost Wolf spirit grey-blue. Bursts end (one-shot tweens); the wolf form
 * loops on alpha/scale only; no screen shake or flash (plan 29 F8).
 */
import type { VFXEffectConfig } from '../../VFXEffectRegistry';
import { ringBlast, shardBurst, glintScatter, wispRise, starFlash, groundCloud } from './burstBuilders';

const TEAL = 0x00b3a4;
const TEAL_HOT = 0x9ff2e8;
const SKY = 0x88ddff;
const SKY_HOT = 0xe6faff;
const VIOLET = 0xb388ff;
const VIOLET_HOT = 0xeee0ff;
const STORM = 0x3d7bff;
const STORM_HOT = 0xc8dcff;
const FLAME = 0xff6600;
const FLAME_HOT = 0xffd080;
const EMBER = 0xffaa22;
const SEA = 0x33ccaa;
const SEA_HOT = 0xc8fff0;
const GREEN = 0x66ff66;
const GREEN_DEEP = 0x1f9e4a;
const SPIRIT = 0xa0b4c8;
const SPIRIT_HOT = 0xe8f2ff;
/** The wolf's body: deep enough to read over the pale player sprite (live VQA). */
const SPIRIT_DEEP = 0x4f78a8;

/** Above the player sprite (depth 100) — see PriestKitVFX's note (live VQA, lane CA). */
const OVER = 102;
/** A stroked ring closing onto the caster — reads from the first frame (the BY reticle grammar). */
function closingRing(color: number, hot: number, radius: number, durationMs: number, scaleTo = 0.5) {
  return [
    { type: 'arc' as const, arcRadius: radius, arcStartAngle: 0, arcEndAngle: 360, width: 3, color: hot, alpha: 0.9, depth: 101,
      tween: { scale: scaleTo, duration: durationMs, ease: 'Sine.easeIn' } },
    { type: 'arc' as const, arcRadius: radius + 6, arcStartAngle: 0, arcEndAngle: 360, width: 7, color, alpha: 0.25, depth: 100,
      tween: { scale: scaleTo, alpha: 0.6, duration: durationMs, ease: 'Sine.easeIn' } },
  ];
}

type Pt = { x: number; y: number };
/** `count` small shapes on an ellipse that each travel to `inner` × their place (a tell closing in). */
function ringIn(count: number, rx: number, ry: number, inner: number, base: { radius: number; color: number; durationMs: number; delayMs?: number }) {
  return Array.from({ length: count }, (_, i) => {
    const a = (i / count) * Math.PI * 2;
    return {
      type: 'circle' as const, radius: base.radius, color: base.color, alpha: 0.95, depth: OVER, glow: true,
      offsetX: Math.round(Math.cos(a) * rx), offsetY: Math.round(Math.sin(a) * ry),
      tween: { x: Math.round(Math.cos(a) * rx * inner), y: Math.round(Math.sin(a) * ry * inner), alpha: 0.35, duration: base.durationMs, ease: 'Sine.easeIn', ...(base.delayMs ? { delay: base.delayMs } : {}) },
    };
  });
}
/** Small shapes rising beside the body, from the feet up (droplets, mist wisps). */
function riseBeside(xs: number[], base: { radius: number; color: number; durationMs: number; travel: number; delayMs?: number }) {
  return xs.map((dx, i) => ({
    type: 'circle' as const, radius: base.radius, color: base.color, alpha: 0.95, depth: OVER, glow: true,
    offsetX: dx, offsetY: 28,
    tween: { y: 28 - base.travel, alpha: 0, duration: base.durationMs, ease: 'Sine.easeOut', delay: (base.delayMs ?? 0) + i * 60 },
  }));
}
/** A hand-authored zigzag (lightning) as a `line` primitive. */
const bolt = (linePoints: Pt[], color: number, lineWidth: number, alpha: number, durationMs: number, delayMs = 0) => ({
  type: 'line' as const, linePoints, lineWidth, color, alpha, depth: 98, glow: true,
  tween: { alpha: 0, duration: durationMs, ease: 'Power2', ...(delayMs ? { delay: delayMs } : {}) },
});

/** Lightning Touch's confirmed hit: a small teal crackle where the bolt met the target. */
const LIGHTNING_TOUCH: VFXEffectConfig = {
  id: 'lightning_touch',
  name: 'Lightning Touch',
  category: 'burst',
  description: 'A small teal crackle where the bolt meets the target: a six-ray star, a white core and a thin ring',
  primitives: [
    starFlash({ points: 6, innerRadius: 3, outerRadius: 13, color: TEAL_HOT, glow: true, spinRad: Math.PI / 6, expandTo: 1.4, durationMs: 200 }),
    ringBlast({ radius: 8, color: TEAL, alpha: 0.5, stroke: { color: TEAL_HOT, width: 2 }, expandTo: 2, durationMs: 220 }),
    glintScatter({ count: 3, spread: 12, color: 0xffffff, radius: 2, durationMs: 160 }),
  ],
};

/** Lightning Bolt's wind-up: sky-blue sparks gather in the hand over 1.5 s. */
const LIGHTNING_BOLT_GATHER: VFXEffectConfig = {
  id: 'lightning_bolt_gather',
  name: 'Lightning Bolt Gather',
  category: 'burst',
  description: 'Sky-blue sparks draw in on the shaman while a crackling orb swells over them and two small arcs flicker at the sides',
  primitives: [
    { type: 'circle', radius: 7, color: SKY, alpha: 0.3, depth: OVER, glow: true,
      stroke: { color: SKY_HOT, width: 2 }, tween: { scale: 2.2, alpha: 0.95, duration: 1450, ease: 'Sine.easeIn' } },
    ...ringIn(6, 52, 38, 0.35, { radius: 2.8, color: SKY_HOT, durationMs: 1400 }),
    ...closingRing(SKY, SKY_HOT, 54, 1450, 0.45),
    { ...bolt([{ x: -26, y: -20 }, { x: -16, y: -10 }, { x: -22, y: -2 }, { x: -10, y: 6 }], SKY_HOT, 2, 0.9, 1400), depth: OVER },
    { ...bolt([{ x: 26, y: -16 }, { x: 18, y: -6 }, { x: 24, y: 2 }, { x: 12, y: 10 }], SKY_HOT, 2, 0.9, 1400), depth: OVER },
  ],
};

/** Lightning Bolt's hit: a forked sky-blue strike on the target. */
const LIGHTNING_BOLT_STRIKE: VFXEffectConfig = {
  id: 'lightning_bolt_strike',
  name: 'Lightning Bolt Strike',
  category: 'burst',
  description: 'A forked sky-blue bolt cracks through the target with a white fork, a bright ring and sparks',
  primitives: [
    bolt([{ x: -4, y: -46 }, { x: 6, y: -30 }, { x: -6, y: -16 }, { x: 4, y: -2 }, { x: 0, y: 8 }], SKY, 5, 0.95, 240),
    bolt([{ x: 6, y: -30 }, { x: 18, y: -20 }, { x: 14, y: -8 }], SKY_HOT, 2, 0.9, 200, 20),
    ringBlast({ radius: 12, color: SKY, alpha: 0.5, stroke: { color: SKY_HOT, width: 2 }, expandTo: 2.2, durationMs: 280 }),
    glintScatter({ count: 6, spread: 20, color: SKY_HOT, radius: 2, durationMs: 240, delayMs: 40 }),
  ],
};

/** Chain Lightning's wind-up: violet sparks orbit in to the hands over 600 ms. */
const CHAIN_LIGHTNING_GATHER: VFXEffectConfig = {
  id: 'chain_lightning_gather',
  name: 'Chain Lightning Gather',
  category: 'burst',
  description: 'Violet sparks close in on the shaman while a violet orb swells over them',
  primitives: [
    ...ringIn(6, 56, 40, 0.4, { radius: 3.2, color: VIOLET_HOT, durationMs: 560 }),
    ...closingRing(VIOLET, VIOLET_HOT, 52, 580, 0.45),
    { type: 'circle', radius: 6, color: VIOLET, alpha: 0.3, depth: OVER, glow: true,
      stroke: { color: VIOLET_HOT, width: 2 }, tween: { scale: 1.8, alpha: 0.95, duration: 560, ease: 'Sine.easeIn' } },
  ],
};

/** Chain Lightning's hit: violet arcs fork out of the target. */
const CHAIN_LIGHTNING_FORK: VFXEffectConfig = {
  id: 'chain_lightning_fork',
  name: 'Chain Lightning Fork',
  category: 'burst',
  description: 'Three violet-white arcs fork out of the target from a white core, with a violet ring',
  primitives: [
    bolt([{ x: 0, y: 0 }, { x: 10, y: -8 }, { x: 16, y: -4 }, { x: 28, y: -14 }], VIOLET, 3, 0.95, 260),
    bolt([{ x: 0, y: 0 }, { x: -12, y: -6 }, { x: -16, y: -16 }, { x: -26, y: -20 }], VIOLET, 3, 0.95, 260, 30),
    bolt([{ x: 0, y: 0 }, { x: 6, y: 12 }, { x: -2, y: 18 }, { x: 8, y: 28 }], VIOLET_HOT, 2, 0.9, 240, 60),
    starFlash({ points: 4, innerRadius: 3, outerRadius: 11, color: 0xffffff, glow: true, expandTo: 1.4, durationMs: 180 }),
    ringBlast({ radius: 12, color: VIOLET, alpha: 0.4, stroke: { color: VIOLET_HOT, width: 2 }, expandTo: 2, durationMs: 260 }),
  ],
};

/** Flame Shock's muzzle: an ember spark at the hand. */
const FLAME_SHOCK_SPARK: VFXEffectConfig = {
  id: 'flame_shock_spark',
  name: 'Flame Shock Spark',
  category: 'burst',
  description: 'A small orange flare and embers jump off the shaman\'s hand',
  primitives: [
    starFlash({ points: 5, innerRadius: 3, outerRadius: 10, color: FLAME_HOT, glow: true, expandTo: 1.5, durationMs: 180 }),
    glintScatter({ count: 4, spread: 10, color: FLAME, durationMs: 200 }),
  ],
};

/** Flame Shock's hit: a flame tongue licks up the target and embers rise. */
const FLAME_SHOCK_SEAR: VFXEffectConfig = {
  id: 'flame_shock_sear',
  name: 'Flame Shock Sear',
  category: 'burst',
  description: 'An orange flame tongue licks up the target from a scorched ring and embers rise off it',
  primitives: [
    { type: 'polygon', polygonPoints: [0, -34, 9, -12, 7, 4, 0, 10, -7, 4, -9, -12], color: FLAME, alpha: 0.9, depth: 97, glow: true,
      stroke: { color: FLAME_HOT, width: 2 }, tween: { y: -10, scaleY: 1.3, alpha: 0, duration: 380, ease: 'Sine.easeOut' } },
    ringBlast({ radius: 10, color: FLAME, alpha: 0.45, stroke: { color: FLAME_HOT, width: 2 }, expandTo: 2, durationMs: 300 }),
    wispRise({ count: 5, spread: 14, radius: 2.2, color: FLAME_HOT, travel: 30, durationMs: 520, glow: true }),
  ],
};

/** Stormstrike's wind-up: storm-blue crackle around the shaman's weapon arm. */
const STORMSTRIKE_CHARGE: VFXEffectConfig = {
  id: 'stormstrike_charge',
  name: 'Stormstrike Charge',
  category: 'burst',
  description: 'Storm-blue sparks close in on the shaman while two blue arcs crackle at the sides over the 500 ms wind-up',
  primitives: [
    ...ringIn(6, 56, 40, 0.5, { radius: 3, color: STORM_HOT, durationMs: 480 }),
    ...closingRing(STORM, STORM_HOT, 52, 480, 0.5),
    { ...bolt([{ x: 30, y: -26 }, { x: 40, y: -14 }, { x: 32, y: -6 }, { x: 44, y: 4 }], STORM_HOT, 3, 0.95, 480), depth: OVER },
    { ...bolt([{ x: -30, y: -22 }, { x: -40, y: -10 }, { x: -32, y: -2 }], STORM, 3, 0.95, 480), depth: OVER },
  ],
};

/** Stormstrike's hit: a thunderclap on the target. */
const STORM_STRIKE: VFXEffectConfig = {
  id: 'storm_strike',
  name: 'Stormstrike Thunderclap',
  category: 'burst',
  description: 'A storm-blue thunderclap on the target: a white flash, a doubled blue ring and thrown sparks',
  primitives: [
    starFlash({ points: 8, innerRadius: 4, outerRadius: 18, color: STORM_HOT, glow: true, expandTo: 1.5, durationMs: 220 }),
    ringBlast({ radius: 14, color: STORM, alpha: 0.55, stroke: { color: STORM_HOT, width: 3 }, expandTo: 2.3, durationMs: 300 }),
    ringBlast({ radius: 14, color: STORM, alpha: 0.25, expandTo: 3, durationMs: 380, delayMs: 80 }),
    shardBurst({ count: 6, spread: 22, color: STORM_HOT, shard: { width: 6, height: 2 }, durationMs: 280, glow: true }),
  ],
};

/** Healing Stream Totem's pulse landing: a tide droplet on the friend it reached. */
const HEALING_STREAM_DROPLET: VFXEffectConfig = {
  id: 'healing_stream_droplet',
  name: 'Healing Stream Droplet',
  category: 'burst',
  description: 'A sea-green droplet falls onto the healed friend and splashes into a small ring',
  primitives: [
    { type: 'polygon', polygonPoints: [0, -9, 5, 0, 4, 5, 0, 7, -4, 5, -5, 0], offsetY: -40, color: SEA, alpha: 0.95, depth: OVER, glow: true,
      stroke: { color: SEA_HOT, width: 1.5 }, tween: { y: 0, alpha: 0, duration: 300, ease: 'Sine.easeIn' } },
    ringBlast({ radius: 14, color: SEA, alpha: 0.45, stroke: { color: SEA_HOT, width: 2 }, expandTo: 2.4, durationMs: 300, delayMs: 220, depth: OVER }),
  ],
};

/** Searing Totem's tick: a small amber ember bolt on the enemy it hit. */
const SEARING_EMBER: VFXEffectConfig = {
  id: 'searing_ember',
  name: 'Searing Ember',
  category: 'burst',
  description: 'A small amber ember strikes the enemy with a short streak and a spark',
  primitives: [
    { type: 'rect', width: 3, height: 18, offsetY: -14, color: EMBER, alpha: 0.95, depth: 97, glow: true,
      tween: { y: 4, alpha: 0, duration: 200, ease: 'Power2' } },
    starFlash({ points: 5, innerRadius: 2, outerRadius: 8, color: FLAME_HOT, glow: true, expandTo: 1.5, durationMs: 200, delayMs: 60 }),
  ],
};

/** Chain Heal's wind-up: green water rises around the shaman over 600 ms. */
const CHAIN_HEAL_GATHER: VFXEffectConfig = {
  id: 'chain_heal_gather',
  name: 'Chain Heal Gather',
  category: 'burst',
  description: 'Green water rises in droplets around the shaman while a green ring brightens at the feet',
  primitives: [
    { type: 'ellipse', width: 58, height: 14, offsetY: 30, color: GREEN, alpha: 0.12, depth: 93,
      stroke: { color: GREEN, width: 2 }, tween: { alpha: 0.5, scale: 1.12, duration: 560, ease: 'Sine.easeIn' } },
    ...riseBeside([-40, 40, -30, 30], { radius: 3.2, color: GREEN, durationMs: 520, travel: 56 }),
    ...closingRing(GREEN_DEEP, GREEN, 52, 560, 0.55),
  ],
};

/** Chain Heal's release: a green surge rolls out to the heal radius (fitted to it). */
const CHAIN_HEAL_SURGE: VFXEffectConfig = {
  id: 'chain_heal_surge',
  name: 'Chain Heal Surge',
  category: 'burst',
  description: 'A green surge and a bright green rim roll out from the shaman to the edge of the heal, droplets riding it',
  primitives: [
    ringBlast({ radius: 60, color: GREEN, alpha: 0.24, stroke: { color: 0xd8ffd8, width: 3 }, expandTo: 2.5, durationMs: 500, ease: 'Sine.easeOut' }),
    ringBlast({ radius: 36, color: GREEN_DEEP, alpha: 0.2, expandTo: 2.6, durationMs: 420, delayMs: 50 }),
    glintScatter({ count: 8, spread: 100, color: GREEN, radius: 3, durationMs: 400, delayMs: 120 }),
  ],
};

/** Chain Heal's landing: a green chain link closes on each healed friend. */
const CHAIN_HEAL_LINK: VFXEffectConfig = {
  id: 'chain_heal_link',
  name: 'Chain Heal Link',
  category: 'burst',
  description: 'Two green links lock together over the healed friend inside a green ring, and droplets rise',
  primitives: [
    { type: 'ellipse', width: 14, height: 22, offsetX: -5, offsetY: -6, color: GREEN, alpha: 0.05, depth: OVER,
      stroke: { color: GREEN, width: 3 }, tween: { alpha: 0, scale: 1.3, duration: 420, ease: 'Power2', delay: 120 } },
    { type: 'ellipse', width: 14, height: 22, offsetX: 5, offsetY: -6, color: GREEN, alpha: 0.05, depth: OVER,
      stroke: { color: 0xd8ffd8, width: 3 }, tween: { alpha: 0, scale: 1.3, duration: 420, ease: 'Power2', delay: 160 } },
    ringBlast({ radius: 16, color: GREEN, alpha: 0.35, stroke: { color: GREEN, width: 2 }, expandTo: 1.9, durationMs: 360 }),
    wispRise({ count: 4, spread: 16, radius: 2.4, color: GREEN, travel: 28, durationMs: 480, glow: true }),
  ],
};

/** Ghost Wolf's wind-up: spirit mist gathers at the shaman's feet over 3 s. */
const GHOST_WOLF_HOWL: VFXEffectConfig = {
  id: 'ghost_wolf_howl',
  name: 'Ghost Wolf Howl',
  category: 'burst',
  description: 'Grey-blue spirit mist pools at the shaman\'s feet and wisps rise in waves until the form takes hold',
  primitives: [
    { ...groundCloud({ width: 70, height: 16, color: SPIRIT, alpha: 0.2, swellTo: 1.3, durationMs: 3000 }),
      tween: { alpha: 0.55, scale: 1.3, duration: 2900, ease: 'Sine.easeIn' } },
    ...riseBeside([-42, 42, -32, 32, -20, 20], { radius: 3.5, color: SPIRIT_HOT, durationMs: 2600, travel: 70, delayMs: 200 }),
    ...closingRing(SPIRIT, SPIRIT_HOT, 56, 2900, 0.6),
  ],
};

/** Ghost Wolf's form: a spectral wolf runs with the shaman while it lasts. */
const GHOST_WOLF_FORM: VFXEffectConfig = {
  id: 'ghost_wolf_form',
  name: 'Ghost Wolf Form',
  category: 'status',
  description: 'A translucent grey-blue wolf silhouette over the shaman with spirit mist at the feet: running faster while it lasts',
  followTarget: 'player',
  primitives: [
    { type: 'ellipse', width: 70, height: 14, offsetY: 30, color: SPIRIT, alpha: 0.28, depth: 93,
      tween: { alpha: 0.45, scale: 1.08, duration: 600, yoyo: true, repeat: -1, ease: 'Sine.easeInOut' } },
    // The wolf: body, head and ears, side profile facing right (flipX mirrors it).
    // Live VQA (lane CA): drawn 1.25× and brighter — at 0.3 alpha the form
    // read as a grey smudge on the pale sprite.
    { type: 'polygon', polygonPoints: [-34, 6, -26, -6, -6, -10, 14, -12, 22, -22, 26, -30, 30, -22, 36, -20, 44, -14, 40, -8, 30, -6, 24, 6, 18, 18, 12, 6, -16, 8, -24, 20, -28, 8].map((n) => Math.round(n * 1.4)),
      color: SPIRIT_DEEP, alpha: 0.55, depth: OVER, glow: true, stroke: { color: SPIRIT_HOT, width: 2.5 },
      tween: { alpha: 0.68, duration: 700, yoyo: true, repeat: -1, ease: 'Sine.easeInOut' } },
    { type: 'circle', radius: 2.8, offsetX: 47, offsetY: -22, color: 0x9fe8ff, alpha: 1, depth: OVER + 1, glow: true },
  ],
};

export const SHAMAN_KIT_VFX: VFXEffectConfig[] = [
  LIGHTNING_TOUCH,
  LIGHTNING_BOLT_GATHER,
  LIGHTNING_BOLT_STRIKE,
  CHAIN_LIGHTNING_GATHER,
  CHAIN_LIGHTNING_FORK,
  FLAME_SHOCK_SPARK,
  FLAME_SHOCK_SEAR,
  STORMSTRIKE_CHARGE,
  STORM_STRIKE,
  HEALING_STREAM_DROPLET,
  SEARING_EMBER,
  CHAIN_HEAL_GATHER,
  CHAIN_HEAL_SURGE,
  CHAIN_HEAL_LINK,
  GHOST_WOLF_HOWL,
  GHOST_WOLF_FORM,
];
